import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import Swal from 'sweetalert2';
import {
  CondicionesCampana,
  LlaveService,
  MiProgramaReferidos,
  ReferidoItem,
  ResumenReferidos,
} from 'src/app/shared/services/llave.service';

/** Cuántos invitados se asoman en la tarjeta antes de mandar al módulo. */
const ULTIMOS_VISIBLES = 3;

/**
 * El aviso de la campaña de referidos en el home del cliente.
 *
 * Es una vitrina, no una segunda pantalla de referidos. Muestra lo que el
 * cliente necesita para actuar sin irse del home —cuánto gana, su link, y si
 * alguno de sus invitados ya cargó— y todo lo demás lleva al módulo completo
 * en `/wallet/client/referidos`. Nada que sea irreversible vive acá: crear la
 * llave es para siempre y su confirmación sigue estando en un solo lugar.
 *
 * CUÁNDO SE MUESTRA. Dos condiciones, y las dos fallan cerradas:
 *
 *  1. **Hay una campaña activa de su país.** Lo dice `miPrograma().visible`:
 *     el interruptor `REFERIDOS_ACTIVO` y una campaña que alcance su
 *     documento (Chile o Venezuela). Ante un error viene en `false`, y no
 *     poder saber si hay campaña no es razón para anunciar una.
 *  2. **Al cliente le queda cupo.** `resumen().cuota` dice si todavía existe
 *     una campaña que pueda pagarle un par más —la asignada que le quede con
 *     cuota, o la global. A quien ya agotó la suya no se le ofrece un
 *     programa que no va a pagarle: eso es prometer de gratis.
 *
 * Ojo con el orden de despliegue: `cuota` es campo nuevo del resumen, así que
 * **el API va antes que el front**. Si el resumen todavía no lo trae, la
 * tarjeta no aparece —que es el lado seguro— en vez de aparecerle a todos.
 *
 * SIN LLAVE. La tarjeta anuncia la campaña igual, pero sin link ni botones de
 * compartir: para usar el programa primero hay que tener el usuario Aguacate,
 * y la única acción que se ofrece es ir a crearlo.
 *
 * Nace plegada, siempre, y no recuerda el estado: mismo criterio que Tasas del
 * Día. El detalle que sólo sirve desplegada —la llave y los últimos
 * invitados— se pide recién al abrir.
 */
@Component({
  selector: 'app-referidos-home',
  templateUrl: './referidos-home.component.html',
  styleUrls: ['./referidos-home.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferidosHomeComponent implements OnInit {
  /**
   * Si este cliente ve referidos: programa encendido y una campaña activa de
   * su país. `null` mientras no se leyó; ante un error viene `visible: false`.
   */
  miPrograma: MiProgramaReferidos | null = null;

  /** Se pide al entrar porque de él sale el cupo, que decide si hay tarjeta. */
  resumen: ResumenReferidos | null = null;

  colapsada = true;

  /** El detalle ya se trajo bien. Con `false`, abrir vuelve a intentarlo. */
  cargado = false;
  cargando = false;
  fallo = false;

  llave: string | null = null;
  ultimos: ReferidoItem[] = [];

  constructor(
    private llaveService: LlaveService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.miPrograma = await this.llaveService.miPrograma();

    // Sin campaña de su país no se pregunta nada más: el home no paga una
    // consulta por una tarjeta que no va a existir.
    if (this.miPrograma.visible) {
      try {
        this.resumen = await this.llaveService.resumen();
      } catch {
        this.resumen = null;
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Campaña encendida y cupo disponible. Cualquier otra cosa —programa
   * apagado, resumen que no cargó, cuota agotada— es no mostrar nada.
   */
  get visible(): boolean {
    return Boolean(this.miPrograma?.visible) && Boolean(this.resumen?.cuota?.disponible);
  }

  /**
   * Las condiciones de la campaña que les tocaría a sus invitados. Salen del
   * resumen, que mira SU campaña y su cupo; `null` o ausente es no prometer.
   */
  get condiciones(): CondicionesCampana | null {
    return this.resumen?.condiciones ?? null;
  }

  /** Lo que gana quien invita, como texto. Vacío si no hay monto que prometer. */
  get bonoTexto(): string {
    return this.llaveService.bonoReferidorTexto(this.condiciones);
  }

  /** Lo que gana el invitado, que puede ser otro monto. */
  get bonoInvitadoTexto(): string {
    return this.llaveService.bonoReferidoTexto(this.condiciones);
  }

  /** «una carga de al menos … en una sola operación». */
  get cargaTexto(): string {
    return this.llaveService.cargaTexto(this.condiciones);
  }

  /** Lo que se le pide a quien invita, y el plazo de cada invitado. */
  get fraseCondiciones(): string {
    return [
      this.llaveService.fraseActividad(this.condiciones),
      this.llaveService.frasePlazo(this.condiciones),
      this.llaveService.alcanceReferidorTexto(this.condiciones),
    ]
      .filter(Boolean)
      .join(' ');
  }

  /** El renglón del encabezado, que es lo único que se ve estando plegada. */
  get titulo(): string {
    return this.bonoTexto
      ? `Gana ${this.bonoTexto} por cada amigo que invites`
      : 'Invita a tus conocidos a Aguacate';
  }

  get link(): string {
    return this.llaveService.linkDe(this.llave);
  }

  /** Lo ganado, por moneda: «5 USDT · 300 Bs.». */
  get ganadoTexto(): string {
    return this.llaveService.totalesTexto(this.resumen, 'liberado');
  }

  /** Invitados que todavía no hicieron su primera carga. */
  get enProceso(): number {
    if (!this.resumen) return 0;
    return Math.max(0, this.resumen.invitados - this.resumen.invitadosQueFondearon);
  }

  /**
   * Cuántas invitaciones más le pagan, si hay tope.
   *
   * `null` es cuota sin tope y entonces no se dice nada: anunciar "te quedan
   * infinitas" no le sirve a nadie.
   */
  get restante(): number | null {
    return this.resumen?.cuota?.restante ?? null;
  }

  // ───────────────────────── Plegado ─────────────────────────

  async alternar(): Promise<void> {
    this.colapsada = !this.colapsada;
    if (!this.colapsada && !this.cargado) await this.cargarDetalle();
  }

  /**
   * La llave y los últimos invitados.
   *
   * El listado sólo tiene sentido con llave creada: sin ella no hay a quién
   * haber invitado. Si falla, la tarjeta lo dice y deja reintentar cerrando y
   * abriendo, en vez de quedarse con un spinner para siempre.
   */
  private async cargarDetalle(): Promise<void> {
    this.cargando = true;
    this.fallo = false;
    this.cdr.markForCheck();

    try {
      const { llave } = await this.llaveService.miLlave();
      this.llave = llave;

      if (llave) {
        const lista = await this.llaveService.misReferidos(1, ULTIMOS_VISIBLES);
        this.ultimos = lista.items;
      }

      this.cargado = true;
    } catch {
      this.fallo = true;
    } finally {
      this.cargando = false;
      this.cdr.markForCheck();
    }
  }

  // ───────────────────────── Invitados ─────────────────────────

  /** La inicial del avatar. Un invitado sin nombre todavía no terminó su alta. */
  inicial(r: ReferidoItem): string {
    const nombre = (r?.nombre || '').trim();
    return nombre ? nombre.charAt(0).toUpperCase() : '·';
  }

  /**
   * En qué anda ese invitado.
   *
   * El texto de `falta` lo redacta el backend y es el mismo que ve el
   * operador: no se reescribe acá. `null` significa que el job todavía no
   * evaluó el vínculo, y entonces la tarjeta no promete nada.
   */
  estado(r: ReferidoItem): string {
    if (r.bonoEstado === 'LIBERADO') return 'Bono acreditado';
    if (r.falta) return r.falta;
    return r.fondeo ? 'Ya hizo su primera carga' : 'Falta su primera carga';
  }

  /** El chip de la derecha: lo cobrado, lo que espera, o lo que ya no llega. */
  chip(r: ReferidoItem): { texto: string; clase: string } {
    if (r.bonoEstado === 'LIBERADO') {
      return { texto: `+${r.montoUsdt} ${r.moneda || 'USDT'}`, clase: 'ok' };
    }
    if (r.bonoEstado === 'VENCIDO' || r.bonoEstado === 'ANULADO') {
      return { texto: 'Sin bono', clase: 'off' };
    }
    return { texto: 'En proceso', clase: 'wait' };
  }

  trackReferido(_: number, r: ReferidoItem): string {
    return `${r.nombre}|${r.registradoAt}`;
  }

  // ───────────────────────── Compartir ─────────────────────────

  async copiarLink(): Promise<void> {
    if (!this.link) return;

    if (await this.llaveService.copiar(this.link)) {
      Swal.fire({ text: 'Link copiado', icon: 'success', timer: 1400, showConfirmButton: false });
      return;
    }

    // Sin permiso de portapapeles: se le muestra el link para que lo copie a
    // mano en vez de dejarlo sin salida.
    Swal.fire({ title: 'Cópialo desde aquí', text: this.link, icon: 'info' });
  }

  /**
   * Abre WhatsApp con el mensaje armado.
   *
   * `wa.me` y no `navigator.share`: en escritorio el diálogo nativo no existe
   * en la mitad de los navegadores, y este es el canal por el que la campaña
   * se comparte de verdad. El módulo completo sigue ofreciendo el compartir
   * nativo para quien lo tenga.
   */
  compartirWhatsapp(): void {
    if (!this.link) return;
    const texto = this.llaveService.textoInvitacion(this.link, this.condiciones);
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener');
  }
}
